import React from 'react';
import ReactDOM, { Root } from 'react-dom/client';

import '@/assets/styles/index.css';
import DocumentConverter from '@/components/DocumentConverter';
import ImageConverter from '@/components/ImageConverter';
import MergePDF from '@/components/MergePDF';
import OrganizePDF from '@/components/OrganizePDF';
import PdfCompressor from '@/components/PdfCompressor';
import UniversalConverter from '@/components/UniversalConverter';
import AppProvider from '@/components/context/AppContext';
import InvalidArgumentError from '@/errors/InvalidArgumentError';
import type {
  CompressorOptions,
  CompressorTypes,
  ConvertersOptions,
  ConvertersTypes,
  MergeOptions,
  MergeTypes,
  OrganizeOptions,
  OrganizeTypes,
  PdfExtraTools,
} from '@/types/index';
import { incrementOperationHistory } from '@/utils/accessControl';
import { MSC_ENVIRONMENTS } from '@/utils/constants';
import { isDev } from '@/utils/miscellaneous';
import reportWebVitals from '@/utils/reportWebVitals';

(function (globalLibrary: PdfExtraTools) {
  let root: Root;
  const validateHtmlContainer = (container: HTMLElement | null) => {
    if (!(container instanceof HTMLElement)) {
      throw new InvalidArgumentError('container', 'HTMLElement');
    }
  };

  const validateConverterOptions = (options: ConvertersOptions) => {
    if (!options['mscEnvironment'] || !MSC_ENVIRONMENTS.includes(options['mscEnvironment'])) {
      throw new InvalidArgumentError('options.mscEnvironment', 'enum(experimental, test, production)');
    }

    if (!options['translations']) {
      throw new InvalidArgumentError('options.translations', '{ [key: string]: string }');
    }
  };

  const renderConverterComponent = (container: HTMLElement, options: ConvertersOptions, type: ConvertersTypes) => {
    root = ReactDOM.createRoot(container);

    const onFinish = () => incrementOperationHistory('conversion');

    root.render(
      <React.StrictMode>
        <AppProvider
          downloadWinHref={options.downloadWinHref}
          mscEnvironment={options.mscEnvironment}
          type={type}
          translations={options.translations}
          onFreeDownloadClick={options.onFreeDownloadClick}
          googleDataLayer={options.googleDataLayer}
        >
          {
            {
              'universal-converter': <UniversalConverter onFinish={onFinish} />,
              'document-converter': <DocumentConverter onFinish={onFinish} />,
              'image-converter': <ImageConverter onFinish={onFinish} />,
            }[type]
          }
        </AppProvider>
      </React.StrictMode>
    );
  };

  const renderCompressionComponent = (container: HTMLElement, options: CompressorOptions, type: CompressorTypes) => {
    root = ReactDOM.createRoot(container);

    const onFinish = () => incrementOperationHistory('compression');

    root.render(
      <React.StrictMode>
        <AppProvider
          downloadWinHref={options.downloadWinHref}
          mscEnvironment={options.mscEnvironment}
          type={type}
          translations={options.translations}
          onFreeDownloadClick={options.onFreeDownloadClick}
          googleDataLayer={options.googleDataLayer}
        >
          <PdfCompressor onFinish={onFinish} />
        </AppProvider>
      </React.StrictMode>
    );
  };

  const renderOrganizeComponent = (container: HTMLElement, options: OrganizeOptions, type: OrganizeTypes) => {
    root = ReactDOM.createRoot(container);

    const onFinish = () => incrementOperationHistory('organize');

    root.render(
      <React.StrictMode>
        <AppProvider
          downloadWinHref={options.downloadWinHref}
          mscEnvironment={options.mscEnvironment}
          type={type}
          translations={options.translations}
          googleDataLayer={options.googleDataLayer}
          webAppUrl={options.webAppUrl}
        >
          <OrganizePDF onFinish={onFinish} />
        </AppProvider>
      </React.StrictMode>
    );
  };

  const renderMergeComponent = (container: HTMLElement, options: MergeOptions, type: MergeTypes) => {
    root = ReactDOM.createRoot(container);

    const onFinish = () => incrementOperationHistory('merge');

    root.render(
      <React.StrictMode>
        <AppProvider
          downloadWinHref={options.downloadWinHref}
          mscEnvironment={options.mscEnvironment}
          type={type}
          translations={options.translations}
          googleDataLayer={options.googleDataLayer}
          webAppUrl={options.webAppUrl}
        >
          <MergePDF onFinish={onFinish} />
        </AppProvider>
      </React.StrictMode>
    );
  };

  globalLibrary.initUniversalConverter = function (container, options) {
    validateHtmlContainer(container);
    validateConverterOptions(options);

    renderConverterComponent(container as HTMLElement, options, 'universal-converter');
  };

  globalLibrary.initDocumentConverter = function (container, options) {
    validateHtmlContainer(container);
    validateConverterOptions(options);

    renderConverterComponent(container as HTMLElement, options, 'document-converter');
  };

  globalLibrary.initImageConverter = function (container, options) {
    validateHtmlContainer(container);
    validateConverterOptions(options);

    renderConverterComponent(container as HTMLElement, options, 'image-converter');
  };

  globalLibrary.initPdfCompressor = function (container, options) {
    validateHtmlContainer(container);
    validateConverterOptions(options);

    renderCompressionComponent(container as HTMLElement, options, 'pdf-compressor');
  };

  globalLibrary.initOrganizePdf = function (container, options) {
    validateHtmlContainer(container);
    validateConverterOptions(options);

    renderOrganizeComponent(container as HTMLElement, options, 'organize-pdf');
  };

  globalLibrary.initMergePdf = function (container, options) {
    validateHtmlContainer(container);
    validateConverterOptions(options);

    renderMergeComponent(container as HTMLElement, options, 'merge-pdf');
  };
})((window.pdfExtraTools = window.pdfExtraTools || {}));

if (isDev()) {
  window.pdfExtraTools.initMergePdf(document.getElementById('root'), {
    mscEnvironment: 'experimental',
    downloadWinHref: 'https://mobisystems.com/api/download?platformId=win&product=mobipdf',
    webAppUrl: process.env.REACT_APP_PDF_WEB_URL,
    translations: {
      'pdfextra_merge_tool.dropzone.choose_files': 'Choose files',
      'pdfextra_merge_tool.dropzone.drag_files_here': 'or drag your files here',
      'pdfextra_organize_tool.dropzone.start_paragraph':
        "Your PDF will be opened automatically. By uploading your file, you agree to our <a href='/terms-of-use' {{attributes}}>Terms and conditions</a>.",
      'pdfextra_organize_tool.errors.only_single_file':
        'Please add only a single file for organize tool. You can organize all files without limits with PDF Extra Desktop.',
      'pdfextra_organize_tool.footer.heading': "Get <span class='tw-font-bold'>PDF Extra</span> Desktop",
      'pdfextra_organize_tool.footer.features_heading': 'to make unlimited changes, plus',
      'pdfextra_organize_tool.footer.feature_1': 'Insert and copy pages',
      'pdfextra_organize_tool.footer.feature_2': 'Split and crop pages',
      'pdfextra_organize_tool.footer.feature_3': 'Extract and rotate pages',
      'pdfextra_organize_tool.footer.feature_4': 'Rearrange and print pages',
      'pdfextra_organize_tool.footer.feature_5': 'Add custom page labels',
      'pdfextra_organize_tool.errors.max_free_conversions':
        'You have only {{MAX_FREE_CONVERSION_PER_DAY}} free conversion per day. Organize files without limit with PDF Extra Desktop.',
      'pdfextra_organize_tool.errors.file_too_large':
        'The file is too large. Maximum allowed size is {{MAX_FILE_SIZE}}. Organize files without limit with PDF Extra Desktop.',
      'pdfextra_organize_tool.errors.not_supported_file_format':
        'Not supported file format. Allowed file formats are {{allowedExtensions}}. Organize files without limit with PDF Extra Desktop.',
    },
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
